import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { SectionCardHeader } from "./ChartCard";
import type { FilterPillSpec } from "./FilterPills";

// Generic word-cloud card. Pass an array of free-text strings and the
// component tokenises, removes stopwords, packs an Archimedean spiral, and
// renders. Reused by Attributions and Demo Conversion. The signed-up filter
// pills, search highlight, and word-limit slider are baked in.

const PRIMARY  = "#2F7A8A";
const MUTED    = "#A5A196";
const ORANGE   = "#E0A84C";
const FG3      = "#7A766C";

const STOPWORDS = new Set([
  "a","an","the","and","or","but","in","on","at","to","for","of","with","by","from",
  "up","about","into","through","i","my","me","we","our","you","your","they","their",
  "it","its","is","are","was","were","be","been","being","have","has","had","do","does",
  "did","not","no","so","if","as","this","that","these","those","via","etc","just",
  "also","very","more","some","any","all","one","two","three","new","get","got","use",
  "used","using","s","t","re","ve","ll","m","d","don","didn","won","can","will","would",
  "could","should","may","might","am","us","him","her","his","she","he",
  "co","ltd","inc","pty",
]);

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((w) => w.length > 2 && !STOPWORDS.has(w));
}

type StaticWord = {
  text: string;
  x: number; y: number;
  hw: number; hh: number;
  size: number;
  value: number;
  fill: string;
  fontWeight: number;
};

const CLOUD_W = 800;
const CLOUD_H = 340;
const CLOUD_COLORS = [PRIMARY, "#5F6FA0", "#52b788", ORANGE, "#ab8bd3", "#6681fe", "#f66ef1", "#7ad068", "#F59F00", "#385bfc"];

function layoutSpiral(words: StaticWord[]): void {
  if (!words.length) return;
  const first = words[0];
  if (first) { first.x = 0; first.y = 0; }

  const aspect = CLOUD_W / CLOUD_H;
  const MAX_X = CLOUD_W / 2 - 4;
  const MAX_Y = CLOUD_H / 2 - 4;

  const ARM_GAP = 4;
  const ANGLE_STEP = 0.13;
  const R_GROWTH = (ARM_GAP * ANGLE_STEP) / (2 * Math.PI);

  const candidates: Array<{ x: number; y: number }> = [{ x: 0, y: 0 }];
  let angle = 0;
  let r = 0;
  const MAX_R = Math.max(MAX_X / aspect, MAX_Y) * 1.2;
  while (r < MAX_R) {
    angle += ANGLE_STEP;
    r += R_GROWTH;
    const x = aspect * r * Math.cos(angle);
    const y = r * Math.sin(angle);
    if (Math.abs(x) > MAX_X || Math.abs(y) > MAX_Y) continue;
    candidates.push({ x, y });
  }

  for (let i = 1; i < words.length; i++) {
    const w = words[i];
    if (!w) continue;
    let placed = false;
    for (const c of candidates) {
      let overlaps = false;
      for (let j = 0; j < i; j++) {
        const p = words[j];
        if (!p) continue;
        if (Math.abs(c.x - p.x) < w.hw + p.hw && Math.abs(c.y - p.y) < w.hh + p.hh) {
          overlaps = true;
          break;
        }
      }
      if (!overlaps) {
        w.x = c.x;
        w.y = c.y;
        placed = true;
        break;
      }
    }
    if (!placed) {
      w.x = Number.NaN;
      w.y = Number.NaN;
    }
  }
}

export function WordCloud({
  texts,
  title,
  subtitle,
  defaultExcluded = [],
  embedded = false,
  headerExtra,
  filterPills,
}: {
  texts: string[];
  title?: string;
  subtitle?: ReactNode;
  defaultExcluded?: string[];
  embedded?: boolean;
  headerExtra?: ReactNode;
  filterPills?: FilterPillSpec[];
}) {
  const [words, setWords] = useState<StaticWord[]>([]);
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set(defaultExcluded));
  const [addInput, setAddInput] = useState("");
  const [wordLimit, setWordLimit] = useState(120);
  const [search, setSearch] = useState("");

  const wordCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of texts) {
      if (!t) continue;
      for (const w of tokenize(t)) {
        counts.set(w, (counts.get(w) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .filter(([text]) => !excluded.has(text))
      .sort((a, b) => b[1] - a[1])
      .slice(0, wordLimit)
      .map(([text, value]) => ({ text, value }));
  }, [texts, excluded, wordLimit]);

  useEffect(() => {
    if (wordCounts.length === 0) { setWords([]); return; }

    const build = () => {
      const maxVal = wordCounts[0]?.value ?? 1;
      const minVal = wordCounts[wordCounts.length - 1]?.value ?? 1;
      const range = Math.max(maxVal - minVal, 1);
      const fontSize = (v: number) => 10 + Math.round(((v - minVal) / range) * 30);

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const w: StaticWord[] = wordCounts.map((row, i) => {
        const size = fontSize(row.value);
        const fontWeight = row.value / maxVal > 0.5 ? 600 : 400;
        let textWidth = size * 0.55 * row.text.length;
        if (ctx) {
          ctx.font = `${fontWeight} ${size}px Inter, sans-serif`;
          textWidth = ctx.measureText(row.text).width;
        }
        return {
          text: row.text,
          x: 0, y: 0,
          size,
          hw: textWidth / 2 + 3,
          hh: size * 0.55 + 3,
          value: row.value,
          fill: CLOUD_COLORS[i % CLOUD_COLORS.length] ?? PRIMARY,
          fontWeight,
        };
      });

      w.sort((a, b) => b.size - a.size);
      layoutSpiral(w);
      setWords(w.filter((x) => !Number.isNaN(x.x)));
    };

    if (document.fonts?.ready) {
      document.fonts.ready.then(build);
    } else {
      build();
    }
  }, [wordCounts]);

  function resetPositions() { setExcluded(new Set(defaultExcluded)); }
  function removeExcluded(word: string) {
    setExcluded((prev) => { const next = new Set(prev); next.delete(word); return next; });
  }
  function addExcluded(word: string) {
    const w = word.trim().toLowerCase();
    if (!w) return;
    setExcluded((prev) => new Set([...prev, w]));
    setAddInput("");
  }

  const searchTerm = search.trim().toLowerCase();
  const showHeader = !embedded;

  const cloud = (
    <svg viewBox={`0 0 ${CLOUD_W} ${CLOUD_H}`} width="100%" style={{ display: "block" }}>
      <g transform={`translate(${CLOUD_W / 2},${CLOUD_H / 2})`}>
        {words.map((w) => {
          const isMatch = searchTerm.length > 0 && w.text.includes(searchTerm);
          const isDimmed = searchTerm.length > 0 && !isMatch;
          return (
            <g
              key={w.text}
              transform={`translate(${w.x},${w.y})`}
              style={{ opacity: isDimmed ? 0.15 : 1, transition: "opacity 0.15s" }}
            >
              {isMatch && (
                <rect
                  x={-w.hw}
                  y={-w.hh}
                  width={w.hw * 2}
                  height={w.hh * 2}
                  rx={3}
                  fill={ORANGE}
                  opacity={0.25}
                />
              )}
              <text
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={w.size}
                fontFamily="Inter, sans-serif"
                fontWeight={w.fontWeight}
                fill={isMatch ? ORANGE : w.fill}
                className="cloud-word"
                style={{ userSelect: "none" }}
              >
                <title>{w.value} mention{w.value === 1 ? "" : "s"}</title>
                {w.text}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );

  const controls = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <i className="ti ti-search" style={{ fontSize: 13, color: "var(--fg-3)" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="highlight word…"
            className="input"
            style={{ width: 120, fontSize: 12, padding: "3px 8px" }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "var(--fg-3)", whiteSpace: "nowrap" }}>Words: {wordLimit}</span>
          <input
            type="range"
            min={20}
            max={200}
            step={10}
            value={wordLimit}
            onChange={(e) => setWordLimit(Number(e.target.value))}
            style={{ width: 90, accentColor: PRIMARY, cursor: "pointer" }}
            title="Number of words shown"
          />
        </div>
        <button
          onClick={resetPositions}
          className="btn btn-sm"
          style={{ color: FG3, padding: "3px 7px" }}
          title="Reset excluded words"
        >
          <i className="ti ti-refresh" />
        </button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
        {Array.from(excluded).map((w) => (
          <span
            key={w}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              background: "var(--bg-2)", border: "1px solid var(--border-default)",
              borderRadius: 12, padding: "2px 8px", fontSize: 12, color: MUTED,
            }}
          >
            {w}
            <button
              onClick={() => removeExcluded(w)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1, color: MUTED, fontSize: 14 }}
              title={`Show '${w}'`}
            >×</button>
          </span>
        ))}
        <form
          onSubmit={(e) => { e.preventDefault(); addExcluded(addInput); }}
          style={{ display: "flex", gap: 4 }}
        >
          <input
            value={addInput}
            onChange={(e) => setAddInput(e.target.value)}
            placeholder="hide word…"
            className="input"
            style={{ width: 100, fontSize: 12, padding: "3px 8px" }}
          />
          <button type="submit" className="btn btn-sm" style={{ color: FG3 }}>Hide</button>
        </form>
      </div>
    </div>
  );

  if (texts.length === 0) {
    return embedded ? <div className="empty" style={{ padding: 24 }}>No data</div> : null;
  }

  if (embedded) {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
          {headerExtra ?? controls}
        </div>
        {cloud}
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      {showHeader && (
        <SectionCardHeader
          title={title ?? ""}
          subtitle={subtitle}
          filterPills={filterPills}
          headerExtra={controls}
        />
      )}
      <div className="card-body" style={{ padding: 0 }}>
        {cloud}
      </div>
    </div>
  );
}
